import { Logger } from '@icommerce/logger';
import * as Sentry from '@sentry/nextjs';
import { useRouter } from 'next/router';
import { useCallback, useMemo } from 'react';
import type { MutableRefObject } from 'react';

export interface Pagination {
  page: number;
  changeActivePage: (newPage: number) => void;
}

export interface PaginationProps {
  scrollTargetRef?: MutableRefObject<HTMLDivElement | null>;
  total?: number;
  onChange?: (page: number) => void;
}

export const usePagination = (props?: PaginationProps) => {
  const { query } = useRouter();
  const router = useRouter();
  const page = useMemo(() => {
    const pageNum = parseInt(query.params?.[0], 10);
    return Number.isNaN(pageNum) || pageNum > props?.total || pageNum < 1 ? 1 : pageNum;
  }, [query.params, props?.total]);

  const changeActivePage = useCallback(
    async (newPage: number) => {
      if (newPage === page) {
        return;
      }

      const newQuery = { ...query };
      // Reset manual or accidental input of zero or negative pages
      newQuery.params = newPage <= 1 ? undefined : [newPage.toString()];
      try {
        await router.push(
          {
            pathname: router.pathname,
            query: newQuery,
          },
          undefined,
          {
            shallow: true,
            scroll: false,
          }
        );
      } catch (error: unknown) {
        Logger.error({ path: router.pathname, query, error }, 'CouldNotChangeActivePage');
        Sentry.captureException(new Error(`Could not router.push - ${error as string}`));
      }

      props?.scrollTargetRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      props?.onChange?.(newPage);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [page, query, props?.scrollTargetRef, router]
  );

  return { page, changeActivePage } as Pagination;
};
